package org.example.project.enginevertrieb

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.example.project.dashboard.PopupEngineCard
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.example.project.settings.*
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

internal val checkpointLabels = mutableStateMapOf<String, String>()
internal fun applyCheckpointLabels(labels: Map<String, String>) {
    checkpointLabels.clear()
    checkpointLabels.putAll(labels)
}
internal data class SalesCheckpoint(val id: String, val defaultLabel: String, val icon: String, val action: String = id, val actionLabel: String = defaultLabel) {
    val label: String get() = checkpointLabels[id] ?: defaultLabel
}
internal val salesCheckpoints = listOf(
    SalesCheckpoint("MY_LEADS", "Meine Leads", "person"),
    SalesCheckpoint("CONTACTED", "Angeschrieben", "linkedin", "DM_SENT", "DM senden"),
    SalesCheckpoint("REPLIED", "Geantwortet", "chat"),
    SalesCheckpoint("MATERIAL_REQUESTED", "Material Wunsch", "material"),
    SalesCheckpoint("MATERIAL_SENT", "Material gesendet", "material", actionLabel = "Material senden"),
    SalesCheckpoint("CALL_BOOKED", "Call gebucht", "kalender", actionLabel = "Call buchen"),
    SalesCheckpoint("IN_CALL", "Im Call", "call"),
    SalesCheckpoint("CALL_DONE", "Call beendet", "call"),
    SalesCheckpoint("APPOINTMENT_BOOKED", "Termin gebucht", "kalender", actionLabel = "Termin buchen"),
    SalesCheckpoint("APPOINTMENT_DONE", "Termin beendet", "webseite"),
    SalesCheckpoint("CLOSING", "Abschlussphase", "leistungen"),
    SalesCheckpoint("CLOSED", "Abgeschlossen", "leistungen"),
    SalesCheckpoint("PARKED", "Aktuell unpassend", "person", actionLabel = "Lead parken"),
    SalesCheckpoint("IN_REVIEW", "In Prüfung", "suche")
)
internal fun checkpointLabel(id: String) = salesCheckpoints.firstOrNull { it.id == id }?.label ?: id
internal val visibleSalesCheckpoints get() = salesCheckpoints.filter { it.id !in setOf("CLOSED", "PARKED", "IN_REVIEW") }.flatMap {
    if (it.id == "REPLIED") listOf(it, salesCheckpoints.first { checkpoint -> checkpoint.id == "IN_REVIEW" }) else listOf(it)
} + listOf(
    salesCheckpoints.first { it.id == "PARKED" },
    SalesCheckpoint("CLOSED_POSITIVE", "Abgeschlossen", "leistungen", actionLabel = "Positiv"),
    SalesCheckpoint("CLOSED_NEGATIVE", "Abgeschlossen", "leistungen", actionLabel = "Negativ")
)
internal fun checkpointMatches(lead: All, checkpoint: String): Boolean = when (checkpoint) {
    "CLOSED_POSITIVE" -> lead.vertriebCheckpoint == "CLOSED" && lead.vertriebAbschlussErgebnis == "Gewonnen"
    "CLOSED_NEGATIVE" -> lead.vertriebCheckpoint == "CLOSED" && lead.vertriebAbschlussErgebnis in setOf("Verloren", "Aussortiert")
    else -> lead.vertriebCheckpoint == checkpoint
}
internal fun checkpointView(lead: All): String = when {
    checkpointMatches(lead, "CLOSED_POSITIVE") -> "CLOSED_POSITIVE"
    checkpointMatches(lead, "CLOSED_NEGATIVE") -> "CLOSED_NEGATIVE"
    else -> lead.vertriebCheckpoint.ifBlank { "MY_LEADS" }
}
internal fun isCheckpointView(id: String) = salesCheckpoints.any { it.id == id } || visibleSalesCheckpoints.any { it.id == id }
internal fun checkpointHasFollowUp(id: String) = id !in setOf("MY_LEADS", "REPLIED", "MATERIAL_REQUESTED", "CALL_BOOKED", "IN_CALL", "APPOINTMENT_BOOKED", "CLOSED", "CLOSED_POSITIVE", "CLOSED_NEGATIVE", "PARKED", "IN_REVIEW")

internal fun openLinkedInCheckpoint(checkpoint: String) {
    val value = js("encodeURIComponent")(checkpoint) as String
    window.location.hash = window.location.hash.substringBefore('?') +
        "?screen=SYSVertriebsDashboard&linkedinCheckpoint=$value&salesNavigation=${kotlin.js.Date.now().toLong()}"
}

@Composable
internal fun LinkedInCheckpointTiles(contacts: List<All>, selected: String, enabled: Boolean,
    deadlines: Map<String, Int>, onSelect: (String) -> Unit) {
    SalesCheckpointTileRow {
        visibleSalesCheckpoints.forEach { checkpoint ->
            val members = contacts.filter { checkpointMatches(it, checkpoint.id) }
            val due = members.count { it.vertriebFollowUpFaellig }
            val hasFollowUp = checkpointHasFollowUp(checkpoint.id)
            SalesCheckpointTile(checkpoint, members.size, if (hasFollowUp) due else null,
                selected == checkpoint.id, enabled,
                if (hasFollowUp) "${checkpoint.label}: ${members.size} Leads, $due Follow-ups f\u00e4llig \u00b7 Frist ${deadlines[checkpoint.id] ?: 4} Tage"
                else "${checkpoint.label}${if (checkpoint.id.startsWith("CLOSED_")) " - ${checkpoint.actionLabel}" else ""}: ${members.size} Leads",
                callStartedAt = if (checkpoint.id == "IN_CALL") members.map { it.vertriebCheckpointAm }.filter { it.isNotBlank() }.minOrNull() else null) { onSelect(checkpoint.id) }
        }
    }
}

@Composable
internal fun LinkedInLeadActions(lead: All, enabled: Boolean, onAction: (String) -> Unit, canSendMessage: Boolean = true, canStartCall: Boolean = false) {
    val wishes = when (lead.vertriebCheckpoint) {
        "REPLIED", "IN_CALL" -> listOf("MATERIAL_REQUESTED", "CLOSING")
        "MATERIAL_SENT" -> listOf("CLOSING")
        "CALL_DONE" -> listOf("CLOSING")
        "APPOINTMENT_DONE" -> listOf("CLOSING")
        else -> emptyList()
    }.filterNot { it == "MATERIAL_REQUESTED" && "MATERIAL_REQUESTED" in lead.vertriebMeilensteine }
    val index = salesCheckpoints.indexOfFirst { it.id == lead.vertriebCheckpoint }
    val bookings = buildList {
        if (index >= salesCheckpoints.indexOfFirst { it.id == "REPLIED" } && index < salesCheckpoints.indexOfFirst { it.id == "CALL_BOOKED" } && "CALL_DONE" !in lead.vertriebMeilensteine) add(All(key = "CALL_BOOKED", label = "Call buchen"))
        if (index >= salesCheckpoints.indexOfFirst { it.id == "REPLIED" } && index < salesCheckpoints.indexOfFirst { it.id == "APPOINTMENT_BOOKED" } && "APPOINTMENT_DONE" !in lead.vertriebMeilensteine) add(All(key = "APPOINTMENT_BOOKED", label = "Termin buchen"))
    }
    val directAction = when (lead.vertriebCheckpoint) {
        "MATERIAL_REQUESTED" -> "MATERIAL_SENT" to "Material senden"
        "CALL_BOOKED" -> "CALL_DONE" to "Call durchgeführt"
        "IN_CALL" -> "CALL_DONE" to "Call beendet"
        "APPOINTMENT_BOOKED" -> "APPOINTMENT_DONE" to "Termin durchgeführt"
        else -> null
    }
    if (lead.vertriebCheckpoint in setOf("CLOSED", "PARKED")) return
    val options = buildList {
        if (canStartCall && lead.vertriebCheckpoint !in setOf("MY_LEADS", "CONTACTED", "CALL_DONE", "IN_CALL"))
            add(All(key = "IN_CALL", label = "Call starten"))
        if (lead.leadQuelle == "Web" && lead.vertriebCheckpoint in setOf("MY_LEADS", "CONTACTED"))
            add(All(key = "CALL_COMPLETED", label = "Gespräch geführt"))
        if (lead.linkedinNetzwerkstatus != "Nicht vernetzt" &&
            (lead.vertriebCheckpoint == "MY_LEADS" || checkpointHasFollowUp(lead.vertriebCheckpoint))) {
            val firstMessage = lead.vertriebCheckpoint == "MY_LEADS"
            add(All(key = if (firstMessage) "DM_SENT" else "FOLLOWUP",
                label = if (firstMessage) "DM senden" else "Follow-up senden",
                aktiv = canSendMessage && (firstMessage || lead.vertriebFollowUpFaellig)))
        }
        directAction?.let { (action, label) -> add(All(key = action, label = label)) }
        addAll(wishes.map { All(key = it, label = checkpointLabel(it)) })
        if (lead.vertriebCheckpoint == "IN_CALL") {
            if ("MATERIAL_REQUESTED" in lead.vertriebMeilensteine && "MATERIAL_SENT" !in lead.vertriebMeilensteine)
                add(All(key = "MATERIAL_SENT", label = "Material senden"))
            if ("APPOINTMENT_BOOKED" in lead.vertriebMeilensteine && "APPOINTMENT_DONE" !in lead.vertriebMeilensteine)
                add(All(key = "APPOINTMENT_DONE", label = "Termin durchgeführt"))
        }
        addAll(bookings)
        if (lead.vertriebCheckpoint != "IN_REVIEW") add(All(key = "IN_REVIEW", label = checkpointLabel("IN_REVIEW")))
        if (index >= 1) add(All(key = "CLOSED", label = "Abschluss"))
        add(All(key = "PARKED", label = checkpointLabel("PARKED")))
    }
    Div({ style { padding(12.px, 0.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(10.px) } }) {
        if (options.isNotEmpty()) AppDropdown(
            options = options, selectedValue = null, onChange = onAction, placeholder = "Weitere Aktionen",
            enabled = enabled, appearance = AppDropdownAppearance.Field, menuMode = AppDropdownMenuMode.Overlay,
            menuOpenUpward = true,
            attrs = { attr("aria-label", "Weitere Aktionen") })
    }
}

@Composable
internal fun LinkedInCheckpointFinish(lead: All, enabled: Boolean, onClose: () -> Unit, onFinish: (String, String) -> Unit) {
    var result by remember(lead.id) { mutableStateOf("Gewonnen") }
    var note by remember(lead.id) { mutableStateOf("") }
    PopupEngineCard(windowKey = "linkedin-checkpoint-abschluss", zIndex = POPUP_ENGINE_MODAL_Z_INDEX,
        widthPx = 680, heightPx = 450, minWidthPx = 420, minHeightPx = 350,
        centered = true, viewportAnchored = true, resizable = true, onClose = { if (enabled) onClose() }) {
        Div({ style { padding(24.px); height(100.percent); overflowY("auto"); boxSizing("border-box") } }) {
            H3 { Text("Abschluss · ${lead.firma}") }
            AppDropdown(options = listOf("Gewonnen", "Verloren", "Aussortiert").map { All(key = it, label = it) }, selectedValue = result,
                onChange = { result = it }, enabled = enabled, attrs = { attr("aria-label", "Abschlussergebnis") })
            TextArea(attrs = { value(note); attr("placeholder", "Abschlussgrund (Pflichtfeld)"); attr("aria-label", "Abschlussgrund"); if (!enabled) disabled(); onInput { note = it.value }; style { width(100.percent); minHeight(130.px); padding(10.px); margin(16.px, 0.px); boxSizing("border-box") } })
            LumaticaButton("Abgeschlossen speichern", { onFinish(result, note.trim()) }, enabled = enabled && note.isNotBlank(), variant = LumaticaButtonVariant.Action)
        }
    }
}

